import React, { useState, useEffect } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnOrderState,
  VisibilityState
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { DataTablePagination } from '@/components/ui/datatablepagination';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { DataTableCardView } from '@/components/ui/DataTableCardView';
import { ArrowLeft, ArrowRight, ArrowUpDown, ChevronDown, ChevronUp, X, Settings } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getUserInfo, saveUserInfo, TableColumnConfigMap, TableColumnConfig, saveTableColumnConfig } from '@/services/userService';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

const MAX_VISIBLE_ARRAY_ITEMS = 3;

function formatColumnHeader(key: string): string {
  if (!key) return '';

  const sanitized = key
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  if (!sanitized) return key;

  return sanitized
    .split(' ')
    .map((word) => {
      if (!word) return word;
      if (word.length === 1) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function ArrayValueRenderer({ items }: { items: any[] }) {
  const [open, setOpen] = React.useState(false);

  if (!items.length) {
    return <span className="text-muted-foreground">(empty)</span>;
  }

  const visibleItems = items.slice(0, MAX_VISIBLE_ARRAY_ITEMS);
  const hiddenItems = items.slice(MAX_VISIBLE_ARRAY_ITEMS);
  const hasHiddenItems = hiddenItems.length > 0;

  const renderBadges = (values: any[]) =>
    values.map((item, index) => (
      <Badge key={`${index}-${typeof item === 'object' ? JSON.stringify(item) : String(item)}`} variant="outline" className="max-w-[200px] truncate">
        {typeof item === 'object' ? JSON.stringify(item) : String(item)}
      </Badge>
    ));

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <div className="flex items-center gap-2">
        <div className="flex flex-wrap gap-1 flex-1">
          {renderBadges(visibleItems)}
        </div>
        {hasHiddenItems && (
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 gap-1 text-foreground"
            >
              <span>{open ? 'Show less' : `+${hiddenItems.length}`}</span>
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        )}
      </div>
      {hasHiddenItems && (
        <CollapsibleContent className="mt-2 flex flex-wrap gap-1">
          {renderBadges(hiddenItems)}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

/**
 * Generic, reusable DataTable component for any data type.
 *
 * Usage:
 *   <DataTable columns={columns} data={data} pageSize={10} />
 *   <DataTable columns={columns} data={data} pageSize={10} selectable onRowSelectionChange={handleSelection} />
 *
 * - Handles sorting, pagination, and filtering via TanStack React Table.
 * - Supports row selection with the selectable prop
 * - Does NOT render any custom search or filter UI.
 * - Only displays the table and pagination.
 */
// DED-1332: Configuration for hiding internal fields and renaming columns
export interface ColumnDisplayConfig {
  /** Column keys to hide from the "Add Column" dropdown */
  hiddenColumns?: string[];
  /** Column key renames: { originalKey: 'Display Name' } */
  columnRenames?: Record<string, string>;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pageSize?: number;
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Callback when row selection changes, provides array of selected rows */
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  /** Callback when a row is clicked */
  onRowClick?: (row: TData, event?: React.MouseEvent) => void;
  /** Optional whitelist of keys that may be added as custom columns */
  availableColumnKeys?: string[];
  /** Optional unique identifier to persist column configuration per table */
  tableId?: string;
  /** Optional mapping of user IDs to names for display */
  userIdToName?: Record<string, string>;
  /** DED-1109: Controlled page index for preserving pagination across data refreshes */
  pageIndex?: number;
  /** DED-1109: Callback when page index changes */
  onPageIndexChange?: (pageIndex: number) => void;
  /** DED-1332: Configuration for hiding internal fields and renaming columns */
  columnDisplayConfig?: ColumnDisplayConfig;
  /** Optional ID for the sorting buttons tour step */
  sortingId?: string;
}

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { toast } from 'sonner';

export function DataTable<TData, TValue>({
  columns,
  data,
  pageSize: defaultPageSize = 5,
  selectable = false,
  onRowSelectionChange,
  onRowClick,
  availableColumnKeys,
  tableId = window.location.pathname.split('/').pop(),
  userIdToName = {},
  pageIndex: controlledPageIndex,
  onPageIndexChange,
  columnDisplayConfig,
  sortingId,
}: DataTableProps<TData, TValue>) {
  const [isLoading, setIsLoading] = React.useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [customColumns, setCustomColumns] = useState<ColumnDef<TData, any>[]>([]);
  const [newColumnKey, setNewColumnKey] = useState('');
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showToolbar, setShowToolbar] = React.useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = React.useState<boolean>(!tableId);
  const lastSavedConfigRef = React.useRef<string | null>(null);

  console.log('[DataTable] Columns', columns)
  console.log('[DataTable] Data', data)
  console.log('[DataTable] pageSize', defaultPageSize)
  console.log('[DataTable] controlledPageIndex', controlledPageIndex)

  // Add selection column if selectable is true
  const allColumns = React.useMemo(() => {
    if (!selectable) return columns;

    const selectionColumn: ColumnDef<TData, any> = {
      id: 'select',
      meta: { headerClassName: 'w-[28px] pl-1 pr-0', cellClassName: 'w-[28px] pl-1 pr-0 text-center', disableDefaultPadding: true },
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    };

    return [selectionColumn, ...columns];
  }, [columns, selectable]);

  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  const resolveColumnId = (columnDef: ColumnDef<TData, any>): string | undefined => {
    const maybeColumn = columnDef as ColumnDef<TData, any> & { id?: string; accessorKey?: string };
    if (typeof maybeColumn.id === 'string') return maybeColumn.id;
    if (typeof maybeColumn.accessorKey === 'string') return maybeColumn.accessorKey;
    return undefined;
  };

  const baseColumnIds = React.useMemo(() => {
    const ids = new Set<string>();
    columns.forEach((columnDef) => {
      const id = resolveColumnId(columnDef as ColumnDef<TData, any>);
      if (id) ids.add(id);
    });
    return ids;
  }, [columns]);

  // Define the user info type with our custom fields
  interface UserInfoWithPreferences {
    uid: string;
    email?: string | null;
    displayName?: string | null;
    photoURL?: string | null;
    emailVerified: boolean;
    // Add our custom fields
    rowsPerPage?: number;
    tableColumnConfigs?: TableColumnConfigMap;
    [key: string]: any; // Allow for other potential user properties
  }

  const createCustomColumn = React.useCallback((columnKey: string): ColumnDef<TData, any> => ({
    id: columnKey,
    accessorKey: columnKey as any,
    header: formatColumnHeader(columnKey) || columnKey,
    cell: ({ getValue }) => {
      const value = getValue();
      if (value === null || value === undefined) return '-';

      if (Array.isArray(value)) {
        return <ArrayValueRenderer items={value} />;
      }

      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
  }), []);

  // Load user's saved rows per page preference
  React.useEffect(() => {
    const loadUserPreference = async () => {
      try {
        const userInfo = await getUserInfo() as UserInfoWithPreferences;
        if (userInfo?.rowsPerPage) {
          setPageSize(userInfo.rowsPerPage);
        }
        // User preferences loaded successfully

        if (tableId && userInfo?.tableColumnConfigs?.[tableId]) {
          const config: TableColumnConfig = {
            columnOrder: userInfo.tableColumnConfigs[tableId].columnOrder || [],
            columnVisibility: userInfo.tableColumnConfigs[tableId].columnVisibility || {},
            customColumnKeys: userInfo.tableColumnConfigs[tableId].customColumnKeys || [],
          };
          setColumnOrder(config.columnOrder);
          setColumnVisibility(config.columnVisibility);
          if (Array.isArray(config.customColumnKeys) && config.customColumnKeys.length) {
            const customDefs = config.customColumnKeys.reduce<ColumnDef<TData, any>[]>((acc, key) => {
              if (!key) return acc;
              if (baseColumnIds.has(key)) return acc;
              if (acc.some((def) => resolveColumnId(def) === key)) return acc;
              const columnDef = createCustomColumn(key);
              acc.push(columnDef);
              return acc;
            }, []);
            if (customDefs.length) {
              setCustomColumns(customDefs);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      } finally {
        setIsLoading(false);
        // User preferences loaded
        if (tableId) {
          setIsConfigLoaded(true);
        }
      }
    };

    loadUserPreference();
  }, [baseColumnIds, createCustomColumn, tableId, columns]);

  // DED-1109: Support controlled pagination - use controlled value if provided, otherwise use internal state
  const [internalPageIndex, setInternalPageIndex] = React.useState(0);
  const isControlled = controlledPageIndex !== undefined;
  const pageIndex = isControlled ? controlledPageIndex : internalPageIndex;
  const setPageIndex = React.useCallback((newPageIndex: number) => {
    if (isControlled && onPageIndexChange) {
      onPageIndexChange(newPageIndex);
    } else {
      setInternalPageIndex(newPageIndex);
    }
  }, [isControlled, onPageIndexChange]);

  // Save rows per page preference when it changes
  const handlePageSizeChange = React.useCallback(async (newPageSize: number) => {
    setPageSize(newPageSize);
    // Reset to first page when page size changes
    setPageIndex(0);

    try {
      await saveUserInfo({ rowsPerPage: newPageSize });
      // toast.success('Rows per page preference saved successfully');
    } catch (error) {
      console.error('Error saving rows per page preference:', error);
      // toast.error('Failed to save rows per page preference. Please try again.');
    }
  }, []);

  // Combine base columns with custom columns
  const allColumnsWithCustom = React.useMemo(() => {
    return [...allColumns, ...customColumns];
  }, [allColumns, customColumns]);

  const resolvedAvailableKeys = React.useMemo(() => {
    if (Array.isArray(availableColumnKeys) && availableColumnKeys.length) {
      return Array.from(new Set(availableColumnKeys.filter(Boolean)));
    }

    const derived = new Set<string>();
    data.forEach((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return;
      }
      Object.keys(row as Record<string, unknown>).forEach((key) => {
        if (key) {
          derived.add(key);
        }
      });
    });

    return Array.from(derived);
  }, [availableColumnKeys, data]);

  const definedColumnIds = React.useMemo(() => {
    return allColumnsWithCustom
      .map((columnDef) => resolveColumnId(columnDef))
      .filter((key): key is string => Boolean(key));
  }, [allColumnsWithCustom]);

  const customColumnIds = React.useMemo(() => {
    return customColumns
      .map((columnDef) => resolveColumnId(columnDef))
      .filter((key): key is string => Boolean(key));
  }, [customColumns]);

  React.useEffect(() => {
    if (!tableId || !isConfigLoaded) return;

    const config: TableColumnConfig = {
      columnOrder: columnOrder,
      columnVisibility: columnVisibility,
      customColumnKeys: customColumnIds,
    };

    const serialized = JSON.stringify(config);
    if (lastSavedConfigRef.current === serialized) return;
    lastSavedConfigRef.current = serialized;

    saveTableColumnConfig(tableId, config).catch((error) => {
      console.error(`[DataTable] Failed to save column config for ${tableId}:`, error);
    });
  }, [tableId, columnOrder, columnVisibility, customColumnIds, isConfigLoaded]);

  // DED-1332: Helper function to get display name for a column (with renames)
  const getColumnDisplayName = React.useCallback((key: string): string => {
    if (columnDisplayConfig?.columnRenames?.[key]) {
      return columnDisplayConfig.columnRenames[key];
    }
    return formatColumnHeader(key);
  }, [columnDisplayConfig?.columnRenames]);

  const selectableCustomKeys = React.useMemo(() => {
    if (!resolvedAvailableKeys.length) return [];
    const existing = new Set(definedColumnIds);
    // Filter out hidden columns from the Add Column dropdown
    const hiddenSet = new Set(columnDisplayConfig?.hiddenColumns || []);
    const filtered = resolvedAvailableKeys.filter((key) => !existing.has(key) && !hiddenSet.has(key));
    return filtered;
  }, [resolvedAvailableKeys, definedColumnIds, columnDisplayConfig?.hiddenColumns]);

  useEffect(() => {
    if (!selectableCustomKeys.length) {
      if (newColumnKey !== '') {
        setNewColumnKey('');
      }
      return;
    }

    if (!selectableCustomKeys.includes(newColumnKey)) {
      setNewColumnKey(selectableCustomKeys[0]);
    }
  }, [selectableCustomKeys, newColumnKey]);

  // DED-XXXX: Reset page index to 0 if current page is out of bounds
  React.useEffect(() => {
    if (!data.length) return;
    const maxPageIndex = Math.max(0, Math.ceil(data.length / pageSize) - 1);
    if (pageIndex > maxPageIndex) {
      console.log('[DataTable] Page index out of bounds, resetting to 0:', { pageIndex, maxPageIndex, dataLength: data.length, pageSize });
      setPageIndex(0);
    }
  }, [data.length, pageSize, pageIndex, setPageIndex]);

  const table = useReactTable({
    data,
    columns: allColumnsWithCustom,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    // DED-1109: Ensure pagination state is properly controlled
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      rowSelection: selectable ? rowSelection : {},
      columnVisibility,
      columnOrder,
      pagination: {
        pageSize,
        pageIndex,
      },
    },
    onPaginationChange: (updater) => {
      const currentState = {
        pageIndex,
        pageSize,
      };

      const newPagination = typeof updater === 'function'
        ? updater(currentState)
        : updater;

      // DED-1109: Update page index state
      setPageIndex(newPagination.pageIndex);

      // Only trigger save if pageSize changed
      if (newPagination.pageSize !== pageSize) {
        handlePageSizeChange(newPagination.pageSize);
      }
    },
    enableRowSelection: selectable,
  });

  // Update parent component when selection changes
  useEffect(() => {
    if (selectable && onRowSelectionChange) {
      const selectedRows = table
        .getFilteredSelectedRowModel()
        .rows.map(row => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, onRowSelectionChange, selectable, table]);

  if (isLoading) {
    return <div className="w-full flex items-center justify-center p-8">Loading table preferences...</div>;
  }

  // Handler to add a custom column
  const handleAddCustomColumn = () => {
    if (!selectableCustomKeys.length) {
      toast.error('No additional columns available');
      return;
    }

    if (!newColumnKey) {
      toast.error('Select a column to add');
      return;
    }

    if (!selectableCustomKeys.includes(newColumnKey)) {
      toast.error('Column is not available to add');
      return;
    }

    const existingColumn = table.getAllColumns().find((col) => col.id === newColumnKey);
    if (existingColumn) {
      toast.error('Column already exists');
      return;
    }

    const columnDef = createCustomColumn(newColumnKey);

    setCustomColumns((prev) => [...prev, columnDef]);
    toast.success(`Column "${newColumnKey}" added`);
  };

  const removeCustomColumn = (columnId: string) => {
    setCustomColumns((prev) =>
      prev.filter((columnDef) => {
        const defId = resolveColumnId(columnDef);
        return defId !== columnId;
      })
    );

    setColumnOrder((prev) => prev.filter((id) => id !== columnId));
    setColumnVisibility((prev) => {
      if (!prev || !(columnId in prev)) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });

    toast.success(`Column "${columnId}" removed`);
  };

  // Handler to move column left
  const moveColumnLeft = (columnId: string) => {
    const currentOrder = table.getState().columnOrder;
    const allColumnIds = table.getAllLeafColumns().map(col => col.id);
    const orderToUse = currentOrder.length > 0 ? currentOrder : allColumnIds;

    const currentIndex = orderToUse.indexOf(columnId);
    if (currentIndex <= 0) return;

    const newOrder = [...orderToUse];
    [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
    setColumnOrder(newOrder);
  };

  // Handler to move column right
  const moveColumnRight = (columnId: string) => {
    const currentOrder = table.getState().columnOrder;
    const allColumnIds = table.getAllLeafColumns().map(col => col.id);
    const orderToUse = currentOrder.length > 0 ? currentOrder : allColumnIds;

    const currentIndex = orderToUse.indexOf(columnId);
    if (currentIndex >= orderToUse.length - 1) return;

    const newOrder = [...orderToUse];
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
    setColumnOrder(newOrder);
  };

  if (isMobile) {
    return <DataTableCardView
      data={table.getFilteredRowModel().rows.map(row => row.original)}
      columns={columns}
      onRowClick={onRowClick}
      userIdToName={userIdToName}
      pageSize={table.getState().pagination.pageSize}
      pageIndex={table.getState().pagination.pageIndex}
      pageCount={table.getPageCount()}
      onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
    />;
  }

  return (
    <div className="w-full space-y-4">
      {/* Scrollable table container */}
      <div className="w-full overflow-x-auto rounded-lg border">
        <Table className="w-full min-w-[700px] rounded-lg table">
            <TableHeader id={sortingId || TOUR_STEP_IDS.TASKS_SORTING_BUTTONS}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={`${((header.column.columnDef as any)?.meta?.disableDefaultPadding) ? '' : 'px-4'} py-2 text-xs font-semibold uppercase bg-muted/60 text-muted-foreground h-10 whitespace-nowrap text-ellipsis ${((header.column.columnDef as any)?.meta?.headerClassName) || ''}`}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <div className={`flex items-center ${header.column.getCanSort() ? 'cursor-pointer select-none hover:text-foreground' : ''}`}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          )}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {(() => {
                const paginationRows = table.getPaginationRowModel().rows;
                const coreRows = table.getCoreRowModel().rows;
                console.log('[DataTable] TableBody render:', {
                  paginationRowsLength: paginationRows?.length,
                  coreRowsLength: coreRows?.length,
                  pageIndex: table.getState().pagination.pageIndex,
                  pageSize: table.getState().pagination.pageSize,
                  totalRows: data.length
                });
                return null;
              })()}
              {table.getPaginationRowModel().rows?.length ? (
                table.getPaginationRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`h-12 ${row.getIsSelected() ? "bg-muted/50" : ""}`}
                    onClick={(e) => {
                      // Don't trigger row click if the click is on a button (like Eye/Edit icons)
                      if (e.target && (e.target as Element).closest('button')) return;
                      onRowClick?.(row.original, e);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className={`${((cell.column.columnDef as any)?.meta?.disableDefaultPadding) ? '' : 'px-4'} py-2 text-sm ${((cell.column.columnDef as any)?.meta?.cellClassName) || 'whitespace-nowrap max-w-xs overflow-hidden text-ellipsis'}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
        </Table>
      </div>
      
      {/* Fixed controls footer - always visible */}
      <div className="flex items-center justify-between px-2 py-4 border-t bg-card/50">
          {/* Left: Selection count or spacer */}
          <div className="w-32">
            {selectable && (
              <div className="text-sm text-muted-foreground">
                {table.getFilteredSelectedRowModel().rows.length} of{' '}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </div>
            )}
          </div>

          {/* Center: Pagination buttons */}
          <div className="flex gap-2">
            {(() => {
              const currentPage = table.getState().pagination.pageIndex + 1;
              const totalPages = table.getPageCount();
              const pages: (number | string)[] = [];

              // Always show first page
              pages.push(1);

              if (totalPages <= 7) {
                // Show all pages if 7 or fewer
                for (let i = 2; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Smart pagination with ellipsis
                if (currentPage > 3) {
                  pages.push('ellipsis-start');
                }

                // Show pages around current page
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);

                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }

                if (currentPage < totalPages - 2) {
                  pages.push('ellipsis-end');
                }

                // Always show last page
                pages.push(totalPages);
              }

              return pages.map((page, index) => {
                if (typeof page === 'string') {
                  return (
                    <span key={page} className="flex items-center px-2 text-muted-foreground">
                      ...
                    </span>
                  );
                }

                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => table.setPageIndex(page - 1)}
                  >
                    {page}
                  </Button>
                );
              });
            })()}
          </div>

          {/* Right: Rows per page selector */}
          <div id={TOUR_STEP_IDS.TASKS_RESULTS_SELECTOR} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
         <DropdownMenu open={showColumnManager} onOpenChange={setShowColumnManager}>
              <DropdownMenuTrigger asChild>
                <Button id={TOUR_STEP_IDS.TASKS_MANAGE_COLUMNS} variant="outline" size="sm" className="h-[36px]">
                  Columns
                  <ChevronDown className="h-4 w-4 text-muted-foreground/60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[300px]">
                <DropdownMenuLabel>Manage Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[300px]">
                  <div className="space-y-1 p-2">
                    {table.getAllLeafColumns().map((column) => {
                      const canHide = column.getCanHide();
                      const isVisible = column.getIsVisible();
                      const columnId = column.id;
                      const isCustom = customColumnIds.includes(columnId);
                      // DED-1332: Get the accessorKey for rename lookup (column.id may be auto-generated)
                      const accessorKey = (column.columnDef as any).accessorKey;
                      const keyForRename = accessorKey || columnId;

                      return (
                        <div key={columnId} className="flex items-center justify-between gap-2 p-1 hover:bg-muted/50 rounded">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Checkbox
                              checked={isVisible}
                              onCheckedChange={(value) => column.toggleVisibility(!!value)}
                              disabled={!canHide}
                              className="shrink-0"
                            />
                            <span className="text-sm truncate">
                              {typeof column.columnDef.header === 'string'
                                ? getColumnDisplayName(column.columnDef.header)
                                : getColumnDisplayName(keyForRename)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => moveColumnLeft(columnId)}
                            >
                              <ArrowLeft className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => moveColumnRight(columnId)}
                            >
                              <ArrowRight className="h-3 w-3" />
                            </Button>
                            {isCustom && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-destructive"
                                onClick={() => removeCustomColumn(columnId)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
                <DropdownMenuSeparator />
                <div className="p-2 space-y-2">
                  <DropdownMenuLabel className="text-xs">Add Column</DropdownMenuLabel>
                  {selectableCustomKeys.length ? (
                    <div className="flex gap-2">
                      <Select value={newColumnKey} onValueChange={setNewColumnKey}>
                        <SelectTrigger className="h-8 text-left text-sm">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectableCustomKeys.map((key) => (
                            <SelectItem key={key} value={key} className="truncate">
                              {getColumnDisplayName(key)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={handleAddCustomColumn}
                        className="h-8 shrink-0"
                      >
                        Add
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">All available columns are already shown.</p>
                  )}
                  {selectableCustomKeys.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Choose from detected keys in the provided data.
                    </p>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
    </div>
  );
}
